import { useEffect, useState } from "react";
import {
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaArrowRight,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const Navbar = ({ darkMode, toggleDarkMode }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  const links = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Courses", path: "/courses" },
    { name: "Services", path: "/services" },
    { name: "Placements", path: "/placements" },
    { name: "Contact", path: "/contact" },
  ];

  /* =========================================================
     Scroll Detection
  ========================================================= */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* =========================================================
     Close Mobile Menu on Route Change
  ========================================================= */

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  /* =========================================================
     Lock Body Scroll When Mobile Menu Is Open
  ========================================================= */

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "";
      return;
    }

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  /* =========================================================
     Close Menu When Switching to Desktop
  ========================================================= */

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setOpen(false);
        document.body.style.overflow = "";
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };

  return (
    <>
      {/* =======================================================
          NAVBAR
      ======================================================= */}

      <nav
        aria-label="Main navigation"
        className="
          fixed
          left-0
          right-0
          top-2
          z-50
          px-2
          sm:top-3
          sm:px-4
        "
      >
        <div
          className={`
            mx-auto
            max-w-7xl
            overflow-visible
            rounded-2xl
            border
            backdrop-blur-2xl
            transition-all
            duration-500
            ${scrolled
              ? `
                  border-gray-200/80
                  bg-white/90
                  shadow-[0_12px_40px_rgba(15,23,42,0.10)]
                  dark:border-gray-700/80
                  dark:bg-gray-900/90
                  dark:shadow-[0_12px_40px_rgba(0,0,0,0.35)]
                `
              : `
                  border-white/60
                  bg-white/75
                  shadow-[0_8px_30px_rgba(15,23,42,0.06)]
                  dark:border-gray-700/60
                  dark:bg-gray-900/70
                `
            }
          `}
        >
          {/* =================================================
              MAIN NAV ROW
          ================================================= */}

          <div
            className="
              flex
              min-h-[64px]
              items-center
              justify-between
              gap-3
              px-3
              py-2
              sm:px-4
              md:min-h-[68px]
              md:px-5
              lg:px-6
            "
          >
            {/* =================================================
                BRAND
            ================================================= */}

            <Link
              to="/"
              aria-label="IA4Solutions Home"
              className="
                group
                flex
                min-w-0
                items-center
                gap-2.5
                sm:gap-3
              "
            >
              {/* Logo */}
              <div
                className="
                  relative
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  bg-white
                  p-0.5
                  shadow-sm
                  ring-1
                  ring-gray-200/80
                  transition-transform
                  duration-300
                  group-hover:scale-105
                  dark:ring-gray-700
                "
              >
                <img
                  src="/images/logos/INNOVATIVE_ACDEMY.png"
                  alt="IA4Solutions Logo"
                  className="
                    h-full
                    w-full
                    rounded-[10px]
                    object-cover
                  "
                />
              </div>

              {/* Brand Text */}
              <div className="min-w-0 leading-none">
                <span
                  className="
                    block
                    truncate
                    text-base
                    font-extrabold
                    tracking-[-0.02em]
                    text-blue-800
                    dark:text-white
                    sm:text-lg
                  "
                >
                  ia4solutions
                </span>

                <span
                  className="
                    mt-1
                    hidden
                    truncate
                    max-w-[260px]
                    text-[9px]
                    font-medium
                    tracking-wide
                    text-gray-500
                    dark:text-gray-400
                    lg:block
                  "
                >
                  Powered by Dr. Aristobacon Institute of Science &amp;
                  Technology Pvt. Ltd.
                </span>

                <span
                  className="
                    mt-0.5
                    block
                    text-[9px]
                    font-medium
                    text-gray-400
                    dark:text-gray-500
                    lg:hidden
                  "
                >
                  Powered by Dr. Aristobacon Institute of Science &amp;
                  Technology Pvt. Ltd.
                </span>
              </div>
            </Link>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <div
              className="
                hidden
                items-center
                gap-1
                md:flex
              "
            >
              {links.map((link) => {
                const active = isActive(link.path);

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    aria-current={active ? "page" : undefined}
                    className={`
                      relative
                      rounded-xl
                      px-3
                      py-2
                      text-[13px]
                      font-semibold
                      transition-all
                      duration-300
                      lg:px-3.5
                      ${active
                        ? `
                            bg-gray-100
                            text-gray-950
                            dark:bg-gray-800
                            dark:text-white
                          `
                        : `
                            text-gray-600
                            hover:bg-gray-100/70
                            hover:text-gray-950
                            dark:text-gray-300
                            dark:hover:bg-gray-800/70
                            dark:hover:text-white
                          `
                      }
                    `}
                  >
                    {link.name}

                    {/* Active indicator */}
                    {active && (
                      <span
                        aria-hidden="true"
                        className="
                          absolute
                          bottom-1
                          left-1/2
                          h-0.5
                          w-5
                          -translate-x-1/2
                          rounded-full
                          bg-gradient-to-r
                          from-orange-500
                          via-pink-500
                          to-indigo-500
                        "
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* =================================================
                DESKTOP ACTIONS
            ================================================= */}

            <div
              className="
                hidden
                items-center
                gap-2
                md:flex
              "
            >
              {/* Dark Mode */}
              <button
                type="button"
                onClick={toggleDarkMode}
                aria-label={
                  darkMode
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                title={
                  darkMode
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-gray-200
                  bg-white/70
                  text-gray-600
                  transition-all
                  duration-300
                  hover:border-orange-300
                  hover:bg-orange-50
                  hover:text-orange-500
                  focus:outline-none
                  focus:ring-2
                  focus:ring-orange-500
                  focus:ring-offset-2
                  dark:border-gray-700
                  dark:bg-gray-800/70
                  dark:text-gray-300
                  dark:hover:border-orange-500/50
                  dark:hover:bg-orange-950/30
                  dark:hover:text-orange-400
                  dark:focus:ring-offset-gray-900
                "
              >
                {darkMode ? (
                  <FaSun
                    aria-hidden="true"
                    className="text-sm"
                  />
                ) : (
                  <FaMoon
                    aria-hidden="true"
                    className="text-sm"
                  />
                )}
              </button>

              {/* Enroll CTA */}
              <Link
                to="/register"
                className="
                  group
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-gradient-to-r
                  from-orange-500
                  to-pink-500
                  px-4
                  py-2.5
                  text-xs
                  font-bold
                  text-white
                  shadow-md
                  shadow-orange-500/15
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:shadow-lg
                  hover:shadow-pink-500/20
                  focus:outline-none
                  focus:ring-2
                  focus:ring-pink-500
                  focus:ring-offset-2
                  dark:focus:ring-offset-gray-900
                  lg:px-5
                  lg:text-sm
                "
              >
                Enroll Now

                <FaArrowRight
                  aria-hidden="true"
                  className="
                    text-[10px]
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </Link>
            </div>

            {/* =================================================
                MOBILE ACTIONS
            ================================================= */}

            <div
              className="
                flex
                items-center
                gap-1.5
                md:hidden
              "
            >
              {/* Dark Mode */}
              <button
                type="button"
                onClick={toggleDarkMode}
                aria-label={
                  darkMode
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  text-gray-700
                  transition-colors
                  hover:bg-gray-100
                  hover:text-orange-500
                  focus:outline-none
                  focus:ring-2
                  focus:ring-orange-500
                  dark:text-gray-200
                  dark:hover:bg-gray-800
                  dark:hover:text-orange-400
                "
              >
                {darkMode ? (
                  <FaSun
                    aria-hidden="true"
                    className="text-sm"
                  />
                ) : (
                  <FaMoon
                    aria-hidden="true"
                    className="text-sm"
                  />
                )}
              </button>

              {/* Menu */}
              <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                aria-controls="mobile-navigation"
                aria-label={
                  open
                    ? "Close navigation menu"
                    : "Open navigation menu"
                }
                className={`
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  transition-all
                  duration-300
                  focus:outline-none
                  focus:ring-2
                  focus:ring-indigo-500
                  ${open
                    ? "bg-gray-100 text-gray-950 dark:bg-gray-800 dark:text-white"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-950 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white"
                  }
                `}
              >
                {open ? (
                  <FaTimes
                    aria-hidden="true"
                    className="text-lg"
                  />
                ) : (
                  <FaBars
                    aria-hidden="true"
                    className="text-lg"
                  />
                )}
              </button>
            </div>
          </div>

          {/* =================================================
              MOBILE MENU
          ================================================= */}

          <div
            id="mobile-navigation"
            className={`
              overflow-hidden
              transition-all
              duration-300
              ease-out
              md:hidden
              ${open
                ? "max-h-[520px] opacity-100"
                : "max-h-0 opacity-0"
              }
            `}
          >
            <div
              className="
                border-t
                border-gray-200/80
                px-3
                pb-4
                pt-2
                dark:border-gray-800
              "
            >
              <div className="space-y-1">
                {links.map((link) => {
                  const active = isActive(link.path);

                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      aria-current={active ? "page" : undefined}
                      className={`
                        flex
                        items-center
                        justify-between
                        rounded-xl
                        px-4
                        py-3
                        text-sm
                        font-semibold
                        transition-all
                        duration-200
                        ${active
                          ? `
                              bg-gradient-to-r
                              from-indigo-50
                              to-purple-50
                              text-indigo-700
                              dark:from-indigo-950/50
                              dark:to-purple-950/40
                              dark:text-indigo-300
                            `
                          : `
                              text-gray-700
                              hover:bg-gray-50
                              hover:text-indigo-600
                              dark:text-gray-200
                              dark:hover:bg-gray-800
                              dark:hover:text-indigo-300
                            `
                        }
                      `}
                    >
                      <span>{link.name}</span>

                      {active && (
                        <span
                          aria-hidden="true"
                          className="
                            h-1.5
                            w-1.5
                            rounded-full
                            bg-indigo-500
                          "
                        />
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* Mobile CTA */}
              <Link
                to="/register"
                className="
                  group
                  mt-3
                  flex
                  min-h-11
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-gradient-to-r
                  from-orange-500
                  to-pink-500
                  px-4
                  text-sm
                  font-bold
                  text-white
                  shadow-md
                  transition-all
                  duration-300
                  hover:shadow-lg
                "
              >
                Enroll Now

                <FaArrowRight
                  aria-hidden="true"
                  className="
                    text-xs
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;